import { supabase, supabasePublic } from '../lib/supabase';

const DIAGNOSTIC_PREFIX = 'DIAGNOSTIC_PAYLOAD::';
const SERVICE_REQUEST_MEDIA_BUCKET = 'service-request-media';

export const serviceRequestService = {
  normalizeServiceType(serviceType, urgency) {
    if (!serviceType) return 'consultation';

    if (serviceType === 'plumbing') {
      return urgency === 'emergency' ? 'plumbing-emergency' : 'plumbing-general';
    }

    if (serviceType === 'electrical-services') {
      return urgency === 'emergency' ? 'electrical-emergency' : 'electrical-general';
    }

    return serviceType;
  },

  async uploadDiagnosticMedia(mediaFiles, userId = 'anonymous', client = supabasePublic) {
    const uploadedMedia = {
      photos: [],
      voice: null,
      uploadErrors: []
    };

    if (!mediaFiles) {
      return uploadedMedia;
    }

    const createStoragePath = (type, fileName) => {
      const safeFileName = fileName?.replace(/[^a-zA-Z0-9.-]/g, '_') || 'file';
      const unique = `${Date.now()}-${Math.random()?.toString(36)?.slice(2, 10)}`;
      return `requests/${userId}/${type}/${unique}-${safeFileName}`;
    };

    const uploadFile = async (file, type) => {
      if (!file) return null;

      const filePath = createStoragePath(type, file?.name);
      const { error: uploadError } = await client?.storage
        ?.from(SERVICE_REQUEST_MEDIA_BUCKET)
        ?.upload(filePath, file, {
          upsert: false,
          contentType: file?.type || undefined
        });

      if (uploadError) {
        throw uploadError;
      }

      const { data: publicUrlData } = client?.storage
        ?.from(SERVICE_REQUEST_MEDIA_BUCKET)
        ?.getPublicUrl(filePath);

      return {
        fileName: file?.name,
        mimeType: file?.type,
        size: file?.size,
        storagePath: filePath,
        publicUrl: publicUrlData?.publicUrl || null
      };
    };

    if (Array.isArray(mediaFiles?.photos) && mediaFiles?.photos?.length > 0) {
      for (const photoFile of mediaFiles?.photos) {
        try {
          const uploadedPhoto = await uploadFile(photoFile, 'photos');
          if (uploadedPhoto) {
            uploadedMedia.photos?.push(uploadedPhoto);
          }
        } catch (error) {
          uploadedMedia.uploadErrors?.push(`Photo "${photoFile?.name}" upload failed: ${error?.message}`);
        }
      }
    }

    if (mediaFiles?.voice) {
      try {
        uploadedMedia.voice = await uploadFile(mediaFiles?.voice, 'voice');
      } catch (error) {
        uploadedMedia.uploadErrors?.push(`Voice note upload failed: ${error?.message}`);
      }
    }

    return uploadedMedia;
  },

  // Create service request (public - no auth required)
  async create(requestData) {
    try {
      // Check if user is authenticated
      const { data: { user } } = await supabase?.auth?.getUser();
      const uploadedMedia = await this.uploadDiagnosticMedia(requestData?.mediaFiles, user?.id || 'anonymous', supabasePublic);
      const normalizedServiceType = this.normalizeServiceType(requestData?.serviceType, requestData?.urgency);

      let requestMessage = requestData?.message || null;
      if (requestData?.diagnosticPayload) {
        requestMessage = `${DIAGNOSTIC_PREFIX}${JSON.stringify({
          ...requestData?.diagnosticPayload,
          media: uploadedMedia
        })}`;
      }

      const buildInsertPayload = (userId) => ({
          user_id: userId,
          name: requestData?.name,
          phone: requestData?.phone,
          email: requestData?.email || null,
          service_type: normalizedServiceType,
          urgency: requestData?.urgency,
          preferred_time: requestData?.preferredTime || null,
          message: requestMessage
      });

      let { data, error } = await supabasePublic?.from('service_requests')?.insert(buildInsertPayload(user?.id || null))?.select()?.single();

      // Compatibility fallback for stricter RLS setups that reject authenticated user_id inserts.
      if (error && /row-level security policy/i?.test(error?.message || '')) {
        const retry = await supabasePublic?.from('service_requests')?.insert(buildInsertPayload(null))?.select()?.single();
        data = retry?.data;
        error = retry?.error;
      }

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get user's own requests
  async getUserRequests() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('service_requests')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get all requests (admin only)
  async getAllRequests() {
    try {
      const { data, error } = await supabase?.from('service_requests')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update request status (admin only)
  async updateStatus(requestId, status, adminNotes = null) {
    try {
      const updates = { status };
      if (adminNotes !== null) {
        updates.admin_notes = adminNotes;
      }

      const { data, error } = await supabase?.from('service_requests')?.update(updates)?.eq('id', requestId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Delete request (admin only)
  async delete(requestId) {
    try {
      const { error } = await supabase?.from('service_requests')?.delete()?.eq('id', requestId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }
};
